import React, { memo, useMemo } from 'react';
import { summarizeEvidence } from '../../utils/sourceDiversity';
import SourceDiversityBadge from './SourceDiversityBadge';
import styles from './evidenceSummary.module.css';

const INDICATOR_TONE_CLASS = {
    success: styles.indicatorSuccess,
    warning: styles.indicatorWarning,
    neutral: styles.indicatorNeutral,
};

function legendColor(legend, key) {
    return legend.find(item => item.key === key)?.color || '#989bad';
}

function EvidenceSummaryBody({ summary, onViewEvidence }) {
    return (
        <>
            {onViewEvidence ? (
                <div className={styles.titleRow}>
                    <span className={styles.srOnly}>Evidence details</span>
                    <button type="button" className={styles.viewEvidenceBtn} onClick={onViewEvidence}>
                        View evidence
                    </button>
                </div>
            ) : null}

            {summary.rows.length > 0 ? (
                <div className={styles.rows}>
                    {summary.rows.map(row => (
                        <div key={row.key} className={styles.row}>
                            <span className={styles.rowLabel}>
                                <span
                                    className={styles.dot}
                                    style={{ background: legendColor(summary.legend, row.key) }}
                                    aria-hidden
                                />
                                {row.label}
                            </span>
                            <span className={styles.rowValue}>
                                {row.count} {row.unit}
                            </span>
                        </div>
                    ))}
                </div>
            ) : null}

            <div className={styles.metaBlock}>
                {summary.workstreams.length > 0 ? (
                    <div className={styles.row}>
                        <span className={styles.rowLabel}>Workstreams</span>
                        <span className={styles.rowValue}>{summary.workstreams.join(', ')}</span>
                    </div>
                ) : null}
                {summary.confidenceLabel ? (
                    <div className={styles.row}>
                        <span className={styles.rowLabel}>Confidence</span>
                        <span className={styles.rowValue}>{summary.confidenceLabel}</span>
                    </div>
                ) : null}
                {summary.grounded ? (
                    <div className={styles.row}>
                        <span className={styles.rowLabel}>Grounded</span>
                        <span className={styles.rowValue}>{summary.grounded}</span>
                    </div>
                ) : null}
            </div>

            {summary.consistency?.label ? (
                <div
                    className={`${styles.consistency} ${
                        summary.consistency.tone === 'success'
                            ? styles.consistencySuccess
                            : styles.consistencyWarning
                    }`.trim()}>
                    {summary.consistency.label}
                </div>
            ) : null}

            <div className={styles.indicators}>
                <SourceDiversityBadge diversity={summary.diversity} />
                {summary.qualityIndicators.map(indicator => (
                    <span
                        key={indicator.key}
                        className={`${styles.indicator} ${INDICATOR_TONE_CLASS[indicator.tone] || INDICATOR_TONE_CLASS.neutral}`}>
                        {indicator.label}
                    </span>
                ))}
            </div>

            <div className={styles.legend} aria-label="Source legend">
                {summary.legend.map(item => (
                    <span key={item.key} className={styles.legendItem}>
                        <span className={styles.dot} style={{ background: item.color }} aria-hidden />
                        {item.label}
                    </span>
                ))}
            </div>
        </>
    );
}

function EvidenceSummary({
    citations = [],
    chunks = [],
    retrievalDebug = {},
    confidence = '',
    evidenceGovernance = null,
    trustStripLabels = [],
    onViewEvidence,
    collapsible = false,
    defaultExpanded = true,
}) {
    const summary = useMemo(
        () =>
            summarizeEvidence({
                citations,
                chunks,
                retrievalDebug,
                confidence,
                evidenceGovernance,
                trustStripLabels,
            }),
        [citations, chunks, retrievalDebug, confidence, evidenceGovernance, trustStripLabels],
    );

    if (!summary.hasContent) return null;

    if (collapsible) {
        return (
            <details
                className={`${styles.evidenceSummary} ${styles.evidenceSummaryAccordion}`.trim()}
                {...(defaultExpanded ? { open: true } : {})}
                aria-label="Evidence summary">
                <summary className={styles.accordionSummary}>
                    <span className={styles.title}>Evidence Summary</span>
                    <span className={styles.accordionHint}>
                        {summary.rows.length > 0
                            ? `${summary.rows.reduce((n, row) => n + (row.count || 0), 0)} items`
                            : 'Details'}
                    </span>
                </summary>
                <div className={styles.accordionBody}>
                    <EvidenceSummaryBody summary={summary} onViewEvidence={onViewEvidence} />
                </div>
            </details>
        );
    }

    return (
        <section className={styles.evidenceSummary} aria-label="Evidence summary">
            <div className={styles.titleRow}>
                <h4 className={styles.title}>Evidence Summary</h4>
                {onViewEvidence ? (
                    <button type="button" className={styles.viewEvidenceBtn} onClick={onViewEvidence}>
                        View evidence
                    </button>
                ) : null}
            </div>
            <EvidenceSummaryBody summary={summary} onViewEvidence={null} />
        </section>
    );
}

export default memo(EvidenceSummary);
